import type { Ehr } from '../../types';
import { PROVIDER_SUPPORT_CONTACT_INFO } from '../../constants';
import * as Sentry from '@sentry/react';
import { isValidEhr, getEhrDisplayName } from '../../utils';
import { generateEhrEmail, generateEhrLastName } from '../onboarding/EhrIntegrationForm/utils';

export type EhrFormField = {
  name: string;
  label: string;
  type?: 'text' | 'password' | 'url';
  placeholder?: string;
  required?: boolean;
};

export type EhrIntegrationConfig = {
  ehrName: string;
  title: string;
  instructions: {
    heading: string;
    description: string;
    bulletPoints: Array<{
      label: string;
      value: string;
    }>;
  };
  formConfig: {
    title: string;
    fields: EhrFormField[];
    note?: {
      title: string;
      description: string;
      permissionsRequired: string[];
    };
  };
  getSuccessMessage: () => string;
};

// Helper function to create EHR integration config with practice name
export const createEhrIntegrationConfig = (
  ehrName: string,
  practiceName: string,
  note?: { title: string; description: string; permissionsRequired: string[] },
): EhrIntegrationConfig => {
  return {
    ehrName,
    title: `Connect with ${ehrName}`,
    instructions: {
      heading: `Create an account for Tandem in ${ehrName}`,
      description: 'Assign this new account the role of Medical Assistant with the following credentials:',
      bulletPoints: [
        {
          label: 'Email:',
          value: generateEhrEmail(practiceName),
        },
        {
          label: 'First Name:',
          value: 'Access Support',
        },
        {
          label: 'Last Name:',
          value: generateEhrLastName(practiceName),
        },
        {
          label: 'DOB:',
          value: '04/15/1995',
        },
      ],
    },
    formConfig: {
      title: `Connect ${ehrName}`,
      fields: [
        {
          name: 'url',
          label: `${ehrName} URL`,
          type: 'text',
          placeholder: 'your-practice-url.com',
          required: true,
        },
        {
          name: 'username',
          label: 'Username',
          type: 'text',
          required: true,
        },
        {
          name: 'password',
          label: 'Password',
          type: 'password',
          required: true,
        },
      ],
      ...(note && { note }),
    },
    getSuccessMessage: () => `${ehrName} credentials submitted`,
  };
};

// Special configuration for Athena that includes department field
export const createAthenaIntegrationConfig = (practiceName: string): EhrIntegrationConfig => {
  return {
    ehrName: 'Athena',
    title: 'Connect with Athena',
    instructions: {
      heading: 'Create an account for Tandem in Athena',
      description: 'Assign this new account the role of Front Office with the following credentials:',
      bulletPoints: [
        {
          label: 'Email:',
          value: generateEhrEmail(practiceName),
        },
        {
          label: 'First Name:',
          value: 'Access Support',
        },
        {
          label: 'Last Name:',
          value: generateEhrLastName(practiceName),
        },
        {
          label: 'DOB:',
          value: '04/15/1995',
        },
      ],
    },
    formConfig: {
      title: 'Connect Athena',
      fields: [
        {
          name: 'url',
          label: 'Athena URL',
          type: 'text',
          placeholder: 'your-practice-url.com',
          required: true,
        },
        {
          name: 'username',
          label: 'Username',
          type: 'text',
          required: true,
        },
        {
          name: 'password',
          label: 'Password',
          type: 'password',
          required: true,
        },
        {
          name: 'department_name',
          label: 'Department Name',
          type: 'text',
          placeholder: 'The Athena Department used by your prescribing providers',
          required: true,
        },
      ],
    },
    getSuccessMessage: () => 'Connected to Athena',
  };
};

// EHRs that need the e-prescribe note
const EHR_WITH_EPRESCRIBE_NOTE: Ehr[] = ['ema_modmed'];

// Helper function to create fallback configuration
const createFallbackConfig = (ehrType: string | null): EhrIntegrationConfig => ({
  ehrName: 'Unsupported EHR',
  title: 'EHR Integration Not Available',
  instructions: {
    heading: '',
    description: ehrType
      ? `Integration with ${ehrType} is not currently supported. Please call us at ${PROVIDER_SUPPORT_CONTACT_INFO.phone} or email ${PROVIDER_SUPPORT_CONTACT_INFO.email} for assistance.`
      : `This EHR system is not currently supported for direct integration. Please call us at ${PROVIDER_SUPPORT_CONTACT_INFO.phone} or email ${PROVIDER_SUPPORT_CONTACT_INFO.email} for assistance.`,
    bulletPoints: [],
  },
  formConfig: { title: '', fields: [] },
  getSuccessMessage: () => 'Support contacted',
});

// Function to get EHR integration config based on EHR type and practice name
export const getEhrIntegrationConfig = (ehrType: Ehr | null, practiceName: string): EhrIntegrationConfig => {
  if (!ehrType) {
    Sentry.captureException(new Error(`EHR integration task created with null EHR type for practice: ${practiceName}`));
    return createFallbackConfig(null);
  }

  if (ehrType === 'athena') {
    return createAthenaIntegrationConfig(practiceName);
  }

  if (!isValidEhr(ehrType)) {
    // Return a fallback configuration for unsupported EHR types
    Sentry.captureException(
      new Error(`EHR integration task created with unsupported EHR type: ${ehrType} for practice: ${practiceName}`),
    );
    return createFallbackConfig(ehrType);
  }

  const displayName = getEhrDisplayName(ehrType);
  const noteTitle = 'Please ensure that the following permissions are enabled:';
  const permissionsRequired = ['Patient charts and lab results', 'After-hours (nights and weekends) access'];
  if (EHR_WITH_EPRESCRIBE_NOTE.includes(ehrType)) {
    permissionsRequired.unshift('E-Prescribe');
  }

  const noteDescription = `In ${displayName}: Admin → Users → [Select Account] → Permissions`;

  return createEhrIntegrationConfig(displayName, practiceName, {
    title: noteTitle,
    description: noteDescription,
    permissionsRequired,
  });
};
